import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ScrollContainer } from 'react-indiana-drag-scroll';

import { ChartWrapper, Chip } from 'src/components';
import { geoChartOptions, geoDataLegendPlugin, processGeoData, prepareGeoData, getChartsFromPreparedData }
  from 'src/scenes/Project/scenes/Analytics/smaAnalyticsHelper';
import { useFetchStatus } from 'src/hooks';


const useStyles = makeStyles(theme => ({
  group: {
    padding: '3px',
    borderRadius: theme.shape.borderRadius,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.common.gray400,
  },
  chartGroup: {
    borderRadius: theme.shape.borderRadius,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.common.gray400,
  },
  scrollbar: {
    '&::-webkit-scrollbar': {
      width: theme.spacing(1),
    },
    '&::-webkit-scrollbar-track': {
      borderRadius: '6px',
      backgroundColor: theme.palette.common.gray300,
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: '6px',
      backgroundColor: '#555',
    },
  },
  label: {
    fontSize: '0.875rem',
  },
  inputLabelShrink: {
    transformOrigin: 'bottom right',
    marginRight: theme.spacing(1) * -1,
    marginTop: '-10px',
    marginBottom: '2px',
  },
  subtitle: {
    fontWeight: 500,
    textTransform: 'uppercase',
    color: theme.palette.common.gray600,
    lineHeight: 1,
  },
}));

const emptyChartData = { labels: [], datasets: [] };

const valuesToChartOptions = [
  'ndwi',
  'ndvi',
];

const chartTypeOptions = [
  { label: 'Histórico', value: 'historical' },
  { label: 'Por año', value: 'byYear' },
];

const GeoInfo = props => {
  const { dataToChart, leftDate, rightDate, geoInfoTypeId } = props;

  const classes = useStyles();

  const { setFetchStatus, setErrorMessage, fetchStatusEnum } = useFetchStatus({
  });

  const [ isChartLoading, setIsChartLoading ] = useState(true);

  const [ selectedChartType, setSelectedChartType ] = useState(chartTypeOptions[0]);

  const [ chartPreview, setChartPreview ] = useState({
    title: '',
    chart: emptyChartData,
    originData: '',
    options: geoChartOptions,
    chartJsType: 'line',
  });


  useEffect(() => {

    const fetchData = async () => {
      setIsChartLoading(true);
      try {
        const processedData = processGeoData(dataToChart, leftDate, rightDate);
        const indicators = geoInfoTypeId ? [ geoInfoTypeId.toUpperCase() ] : [];
        const preparedData = prepareGeoData(processedData, [ 'historical', 'byYear' ], indicators);
        const chart = getChartsFromPreparedData(preparedData, selectedChartType.value, indicators);
        setChartPreview(prev => ({ ...prev, chart, originData: preparedData }));
        setIsChartLoading(false);
      } catch (e) {
        if (e.message === 'Se canceló la petición de datos') {
          return;
        }
        setFetchStatus(fetchStatusEnum.ERROR);
        setErrorMessage('No se pudieron cargar los datos del gráfico');
      }
    };
    fetchData();

    // eslint-disable-next-line
  }, [ selectedChartType.value, `${leftDate} ${rightDate}` ]);

  return (
    valuesToChartOptions.includes(geoInfoTypeId) ?
      <>
        <Box display="flex" flexDirection="column">
          <Box mb={0.5} display="flex" alignItems="center">
            <Typography className={classes.subtitle} variant="body2" component="span">
              Evolución área de trabajo
            </Typography>
            <Box flexGrow={1} />
            <Box display="flex" className={classes.group} p={0.5} ml={1}>
              <Chip
                color={selectedChartType.value === 'historical' ? 'secondary.main' : 'gray'}
                label={chartTypeOptions[0].label}
                size="small"
                onClick={() => setSelectedChartType(chartTypeOptions[0])}
              />
              <Box mx={0.5} />
              <Chip
                color={selectedChartType.value === 'byYear' ? 'secondary.main' : 'gray'}
                label={chartTypeOptions[1].label}
                size="small"
                onClick={() => setSelectedChartType(chartTypeOptions[1])}
              />
            </Box>
          </Box>
          <Box className={classes.chartGroup} p={2}>
            {!isChartLoading ?
              <>
                <Box textAlign="center" display="flex" justifyContent="center">
                  <Typography variant="h6">{chartPreview.title}</Typography>
                </Box>
                <ScrollContainer hideScrollbars={false} className={classes.scrollbar}>
                  <Box id="legend-container" maxHeight={104} />
                </ScrollContainer>
                <Box height="240px">
                  <ChartWrapper
                    type={chartPreview.chartJsType}
                    options={chartPreview.options}
                    data={chartPreview.chart}
                    plugins={[ geoDataLegendPlugin(selectedChartType) ]}
                  />
                </Box>
              </>
              :
              <Box display="flex" alignItems="center" flexDirection="column">
                <CircularProgress size={25} thickness={4.8} />
                <Box mt={0.5}>
                  <Typography variant="body2"><strong>Cargando gráfico...</strong></Typography>
                </Box>
              </Box>
            }
          </Box>
        </Box>
      </> : <></>
  );
};

GeoInfo.propTypes = {
  dataToChart: PropTypes.array.isRequired,
  leftDate: PropTypes.string.isRequired,
  rightDate: PropTypes.string.isRequired,
  geoInfoTypeId: PropTypes.string,
};


export { GeoInfo };
